import { hasYesOrNo, checkValue } from 'models/validate';
import locationAddressModelValidator from 'forms/shared/validators/models/location/models/address';
import phoneModelValidator from 'forms/shared/validators/models/phone/phone';
import emailModelValidator from 'forms/shared/validators/models/pvqEmail';
import name from 'forms/shared/validators/models/name/name';
// import { drugTypes } from 'constants/enums/substanceOptions'
import { DEFAULT_LATEST, THIS_MONTH } from 'constants/dateLimits';
import { ValidateJSModel } from 'forms/shared/types/validatejs';
import { substanceDrugTypeValidator } from 'forms/sections/substance/shared/drugTypeValidator';
import { REQUIRED } from 'constants/errors';
import { getSafeDateTime, isSafeDateLater, setToStartOfMonth } from 'helpers/date';
import { formConfig } from 'config/forms';
import { isPVQForm } from 'helpers/form';
import { SafeDateTime } from 'helpers/types';
import { drugTypes } from 'constants/enums/substanceOptions';
import pvqNameModelValidator from 'forms/shared/validators/models/pvqName/name';
import pvqLocationModelValidator from 'forms/shared/validators/models/location/pvqLocation';
import { isSafeUnitedStates } from 'helpers/locations';
import { SubstanceDrugsMandatoryTreatmentItemModel } from '../types';

export const substanceDrugsMandatoryTreatmentItemValidator: ValidateJSModel<SubstanceDrugsMandatoryTreatmentItemModel> = {
  DrugType: (value, attributes, attributeName, options = {}) => {
    const isPVQ = isPVQForm(options?.formType);
    if (isPVQ) {
      return {};
    }
    if (checkValue(attributes?.ActionTaken, 'Yes')) {
      return substanceDrugTypeValidator;
    }

    return {};
  },
  DrugTypes: (value, attributes, attributeName, options = {}) => {
    const isPVQ = isPVQForm(options?.formType);
    if (isPVQ) {
      if (attributes?.DueToDrugs?.value === 'Yes') {
        return {
          presence: true,
          array: {
            validator: {
              presence: true,
              inclusion: drugTypes,
            },
            length: { minimum: 1, message: REQUIRED },
          },
        };
      }
    }
    return {};
  },
  DrugTypeOther: (value, attributes, attributeName, options = {}) => {
    const isPVQ = isPVQForm(options?.formType);
    if (isPVQ) {
      if (attributes?.DrugTypes?.values?.includes('Other')) {
        return { presence: true, hasValue: true };
      }
    }
    return {};
  },
  // TODO - add this back after fixing DrugType structure
  /*
  DrugType: { presence: true, hasValue: { validator: { inclusion: drugTypes } } },
  DrugTypeExplanation: (value, attributes) => {
    if (attributes?.DrugType && attributes?.DrugType.value === 'Other') {
      return { presence: true, hasValue: true }
    }
    return {}
  },
  */
  Explanation: (value, attributes, attributeName, options = {}) => {
    const isPVQ = isPVQForm(options?.formType);
    if (isPVQ) {
      return {};
    }
    return { presence: true, hasValue: true };
  },
  ActionTaken: { presence: true, hasValue: { validator: hasYesOrNo } },
  OrderedBy: (value, attributes, attributeName, options = {}) => {
    const isPVQ = isPVQForm(options?.formType);
    if (isPVQ) {
      return {};
    }
    const length = { minimum: 1, maximum: 5, tooShort: REQUIRED };

    if (value && value.values && value.values.includes('None')) {
      length.maximum = 1;
    }

    return {
      array: {
        presence: { allowEmpty: false },
        length,
      },
    };
  },
  OrderedBySingle: (value, attributes, attributeName, options = {}) => {
    const isPVQ = isPVQForm(options?.formType);
    if (isPVQ) {
      return { presence: true, hasValue: true };
    }
    return {};
  },
  OrderedBySingleOther: (value, attributes, attributeName, options = {}) => {
    const isPVQ = isPVQForm(options?.formType);
    if (isPVQ) {
      if (attributes?.OrderedBySingle?.value === 'Other') {
        return { presence: true, hasValue: true };
      }
    }
    return {};
  },
  TreatmentProvider: (value, attributes, attributeName, options = {}) => {
    const isPVQ = isPVQForm(options.formType);
    if (checkValue(attributes?.ActionTaken, 'Yes')) {
      if (isPVQ) {
        if (attributes?.TreatmentProviderNotApplicable?.applicable !== false) {
          return {
            presence: true,
            model: {
              validator: pvqNameModelValidator,
              requireMiddleName: false
            }
          };
        }

        return {};
      }

      return { presence: true, model: { validator: name, hideMiddleName: true } };
    }
    return {};
  },
  TreatmentProviderAddress: (value, attributes, attributeName, options) => {
    const isPVQ = isPVQForm(options?.formType);
    if (checkValue(attributes?.ActionTaken, 'Yes')) {
      if (isPVQ) {
        return {
          location: {
            requireStreet: isSafeUnitedStates(value),
            requireMilitaryDiplomaticInstallationName: true,
            requireApoFpoDpoPostcode: !isSafeUnitedStates(value),
            validator: pvqLocationModelValidator,
          },
          presence: true,
        };
      }
      return { presence: true, location: { validator: locationAddressModelValidator } };
    }
    return {};
  },
  TreatmentProviderTelephone: (value, attributes, attributeName, options) => {
    const isPVQ = isPVQForm(options?.formType);
    if (isPVQ) {
      return {};
    }
    if (checkValue(attributes?.ActionTaken, 'Yes')) {
      return { presence: true, model: { validator: phoneModelValidator, requireNumber: true } };
    }
    return {};
  },
  TreatmentDates: (value, attributes, attributeName, options = {}) => {
    if (checkValue(attributes?.ActionTaken, 'Yes')) {
      const formTypeConfig = options?.formType && formConfig[options?.formType as keyof typeof formConfig];
      const years: any = formTypeConfig && formTypeConfig.SUBSTANCE_DRUG_TREATMENT_YEARS;

      const beyondRequired: SafeDateTime = getSafeDateTime(THIS_MONTH);
      let useDuration = false;

      if (years && beyondRequired?.valid && typeof years === 'number') {
        beyondRequired.datetime = beyondRequired.datetime.minus({ years });
        useDuration = !value?.present && isSafeDateLater(beyondRequired, setToStartOfMonth(getSafeDateTime(value?.to)));
      }

      return {
        presence: true,
        daterange: {
          requireYear: false,
          earliest: useDuration ? setToStartOfMonth(beyondRequired).datetime : setToStartOfMonth(options?.applicantBirthdate),
          tooEarly: useDuration ? 'error.beforeDuration' : 'error.applicantMin',
          latest: DEFAULT_LATEST
        },
      };
    }
    return {};
  },
  TreatmentCompleted: (value, attributes) => {
    if (checkValue(attributes?.ActionTaken, 'Yes')) {
      return { presence: true, hasValue: { validator: hasYesOrNo } };
    }
    return {};
  },
  NoTreatmentExplanation: (value, attributes, attributeName, options) => {
    if (
      attributes?.ActionTaken?.value === 'Yes'
      && attributes?.TreatmentCompleted?.value === 'No'
    ) {
      return { presence: true, hasValue: true };
    }

    return {};
  },
  NoActionTakenExplanation: (value, attributes) => {
    if (checkValue(attributes?.ActionTaken, 'No')) {
      return { presence: true, hasValue: true };
    }
    return {};
  },
  DueToPrescription: (value, attributes, attributeName, options = {}) => {
    const isPVQ = isPVQForm(options?.formType);
    if (isPVQ) {
      return { presence: true, hasValue: { validator: hasYesOrNo } };
    }
    return {};
  },
  PrescriptionDrug: (value, attributes, attributeName, options = {}) => {
    const isPVQ = isPVQForm(options?.formType);
    if (isPVQ) {
      if (attributes?.DueToPrescription?.value === 'Yes') {
        return { presence: true, hasValue: true };
      }
    }
    return {};
  },
  DueToDrugs: (value, attributes, attributeName, options = {}) => {
    const isPVQ = isPVQForm(options?.formType);
    if (isPVQ) {
      return { presence: true, hasValue: { validator: hasYesOrNo } };
    }
    return {};
  },
  TreatmentProviderInPractice: (value, attributes, attributeName, options = {}) => {
    const isPVQ = isPVQForm(options?.formType);
    if (isPVQ && attributes?.ActionTaken?.value === 'Yes') {
      return { presence: true, hasValue: { validator: hasYesOrNo } };
    }
    return {};
  },
  TreatmentProviderPracticeNameNotApplicable: {},
  TreatmentProviderPracticeName: (value, attributes, attributeName, options = {}) => {
    const isPVQ = isPVQForm(options?.formType);
    if (isPVQ && attributes?.ActionTaken?.value === 'Yes') {
      return { presence: true, hasValue: true };
    }
    return {};
  },

  TreatmentProviderTelephones: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType) && attributes?.ActionTaken?.value === 'Yes') {
      return {
        presence: true,
        branchCollection: {
          validator: {
            Telephone: {
              model: {
                validator: phoneModelValidator,
                requireNumberType: true
              },
            },
          },
        },
      };
    }
    return {};
  },
  TreatmentProviderEmails: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType) && attributes?.ActionTaken?.value === 'Yes') {
      return {
        presence: true,
        branchCollection: {
          validator: {
            Email: {
              model: {
                validator: emailModelValidator,
                requireEmailType: false,
              },
            },
          },
        },
      };
    }
    return {};
  },
  TreatmentStatus: (value, attributes, attributeName, options = {}) => {
    if (
      isPVQForm(options?.formType)
      && attributes?.ActionTaken?.value === 'Yes'
      && attributes.TreatmentCompleted?.value === 'No'
    ) {
      return { presence: true, hasValue: true };
    }
    return {};
  },
  Comments: {},
  OrderedByComments: {},
  NoActionTakenComments: {},
  NoTreatmentComments: {},
};
